import React, { useEffect, useState, useRef } from 'react'
import 'regenerator-runtime/runtime'
import MomentTz from 'moment-timezone'
import { extendMoment } from 'moment-range'
const moment = extendMoment(MomentTz)
import styled from 'styled-components'

import { Grid, Row, Col } from '@zendeskgarden/react-grid'
import { MD, XL, XXXL } from '@zendeskgarden/react-typography'
import { Message } from '@zendeskgarden/react-forms'
import { Spinner, Skeleton } from '@zendeskgarden/react-loaders'
import { Field, Label, Toggle } from '@zendeskgarden/react-forms'

import Footer from './Footer'
import Header from './Header'
import ServiceList from './ServiceList'
import ServiceSummaryBanner from './ServiceSummaryBanner'
import MaintenanceBanner from './MaintenanceBanner'
import SubDomain from './SubDomain'
import SystemStatus from './SystemStatus'
import ArchiveAnchor from './ArchiveAnchor'
import { useStateWithLocalStorage } from './localStorage'
import PastIncidents from './PastIncidents'
import { fetchFeatures, isFeatureOn } from '../../utils/features'
import { fetchWithTimeout, handleResponseErrors } from '../../utils/http'
import { getQueryVariable } from './utils'
import { sanitizeSubdomainXSS } from '../../utils/sanitize'

const StyledLocationDiv = styled(Message) `
  color: secondary
  color: grey
  width: 890px
  font-size: 12px
`

const StyledField = styled(Field)`
  margin-bottom: 20px
`

const initFromBackendFlashParams = (flashNotice, flashError) => {
  if (flashError) {
    return { type: "error", message: flashError, closeable: true }
  } else if (flashNotice) {
    return { type: "success", message: flashNotice, closeable: true }
  }
  return {}
}

const App = ({ flashNotice, flashError }) => {
  const [flash, setFlash] = useState(initFromBackendFlashParams(flashNotice, flashError))
  const [loadingFeatures, setLoadingFeatures] = useState(true)
  const [loadingMaintenanceIncidents, setLoadingMaintenanceIncidents] = useState(true)
  const [loadingIncidents, setLoadingIncidents] = useState(true)
  const [loadingServices, setLoadingServices] = useState(true)
  const [loadingPastIncidents, setLoadingPastIncidents] = useState(true)
  const [features, setFeatures] = useState({})
  const [environment, setEnvironment] = useState("")
  const [services, setServices] = useState([])
  const [location, setLocation] = useState(null)
  const [incidents, setIncidents] = useState([])
  const [incidentServices, setIncidentServices] = useState([])
  const [subdomain, setSubdomain] = (getQueryVariable('subdomain') && useState(getQueryVariable('subdomain'))) || useStateWithLocalStorage('subdomain')
  const [isLeanDisplay, setIsLeanDisplay] = useState(true)
  const intervalRef = useRef(null)
  const timerRef = useRef(null)
  const [pastIncidents, setPastIncidents] = useState([])
  const [maintenanceIncidents, setMaintenanceIncidents] = useState([])
  const [pageNumber, setPageNumber] = useState(1)
  const [pageTotal, setPageTotal] = useState(1)
  const [sspServicesApiTimeout, setSspServicesApiTimeout] = useState(300000)
  const [sspIncidentsApiTimeout, setSspIncidentsApiTimeout] = useState(300000)
  const [sspPastIncidentsApiTimeout, setSspPastIncidentsApiTimeout] = useState(300000)
  const [sspMaintenanceApiTimeout, setSspMaintenanceApiTimeout] = useState(300000)
  const [dateOffsetBugfixEnabled, setDateOffsetBugfixEnabled] = useState(false)
  const [bannerValue, setBannerValue] = useState([])

  const [rangeOffset, setRangeOffset] = useState(0)

  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone
  const subdomainPopulated = (subdomain.trim() !== '')
  const asOfDate = moment.utc()
  const daysBack = 90

  const setSuccessFlash = message => {
    setFlash({ type: "success", message })
  }

  const setErrorFlash = (message, closeable = true) => {
    setFlash({ type: "error", message, closeable })
  }

  const removeFlash = () => {
    if (Object.keys(flash).length !== 0) {
      setFlash({})
    }
  }

  useEffect(() => {
    if (flash.constructor === Object && Object.keys(flash).length !== 0 && flash.type !== "error") {
      setTimeout(() => {
        removeFlash()
      }, 5000)
    }
  }, [flash])

  useEffect(() => {
    fetchFeatures(setEnvironment, setFeatures, setLoadingFeatures)
  }, [])

  useEffect(() => {
    if (isFeatureOn('date_offset_bugfix')(features)) {
      setDateOffsetBugfixEnabled(true)
    }

    fetchStatus()
  }, [features])

  useEffect(() => {
    if (features && features.data && features.meta) {
      const metaServicesApiTimeout = features.meta.settings.find(s => s.key === 'sspServicesApiTimeout')
      const metaIncidentsApiTimeout = features.meta.settings.find(s => s.key === 'sspIncidentsApiTimeout')
      const metaPastIncidentsApiTimeout = features.meta.settings.find(s => s.key === 'sspPastIncidentsApiTimeout')
      const metaMaintenanceApiTimeout = features.meta.settings.find(s => s.key === 'sspMaintenanceApiTimeout')
      setSspServicesApiTimeout(metaServicesApiTimeout && metaServicesApiTimeout.val)
      setSspIncidentsApiTimeout(metaIncidentsApiTimeout && metaIncidentsApiTimeout.val)
      setSspPastIncidentsApiTimeout(metaPastIncidentsApiTimeout && metaPastIncidentsApiTimeout.val)
      setSspMaintenanceApiTimeout(metaMaintenanceApiTimeout && metaMaintenanceApiTimeout.val)
    }
  }, [features])

  useEffect(() => {
    function sendToGA() {
      gtag('config', 'G-5QQF7PXRBE')
    }

    function sendPodToGA(podId) {
      gtag('event', 'Pods', {
        event_category: 'Data',
        event_label: podId.toString()
      })
    }

    if (features && features.data && services && services.length > 0) {
      sendToGA()
      if (location.data.id !== "0") {
        sendPodToGA(location.data.attributes.podId)
      }
    }
  }, [location])

  const searchError = errorMessage => {
    setErrorFlash(errorMessage, false)
    setServices([])
    setLocation(null)
  }

  const pageError = (errorMessage, pageArea, onGeneralError) => {
    const isTimeoutError = errorMessage === 'The user aborted a request.'
    if (isTimeoutError) {
      setErrorFlash('Page request timed out. Try refreshing in a moment.', false)
      setServices([])
      setIncidents([])
      setIncidentServices([])
      setMaintenanceIncidents([])
    } else {
      onGeneralError(errorMessage)
    }
  }

  async function getBanner() {
    const sspBannerUrl = 'api/ssp/banner'
    try {
      const response = await fetchWithTimeout(sspBannerUrl)
      const body = await response.json()
      handleResponseErrors(response, body)
      if (body && body.settings && body.settings.length > 0) {
        setBannerValue(body.settings[0].val)
      }
    } catch (error) {
      pageError(error.message, 'banner', () => setErrorFlash(error.message, false))
    }
  }

  async function fetchServices() {
    // console.log('fetching services')

    const requestOptions = { timeout: sspServicesApiTimeout }
    let servicesUrl = "api/ssp/services.json"

    if (subdomainPopulated) {
      servicesUrl += `?subdomain=${subdomain}`
    }

    try {
      setLoadingServices(true)
      const serviceResponse = await fetchWithTimeout(servicesUrl, requestOptions)
      const body = await serviceResponse.json()
      handleResponseErrors(serviceResponse, body)
      setServices(body.data)
      setLocation(body.meta.location)
    } catch (error) {
      pageError(error.message, 'services', () => searchError(error.message))
      clearInterval(intervalRef.current)
      clearTimeout(timerRef.current)
      setServices([])
    } finally {
      setLoadingServices(false)
    }
  }

  async function fetchIncidents() {
    // console.log('fetching incidents')

    const requestOptions = { timeout: sspIncidentsApiTimeout }
    let incidentsUrl = `api/ssp/incidents.json?as_of_date=${asOfDate.format('YYYY-MM-DD')}&days_back=${daysBack}`

    if (subdomainPopulated) {
      incidentsUrl += `&subdomain=${subdomain}`
    }

    try {
      setLoadingIncidents(true)
      const incidentResponse = await fetchWithTimeout(incidentsUrl, requestOptions)
      const body = await incidentResponse.json()
      handleResponseErrors(incidentResponse, body)
      setIncidents(body.data)
      setIncidentServices(body.included.filter(i => i.type === 'incidentService'))
    } catch (error) {
      pageError(error.message, 'incidents', () => setErrorFlash(error.message, false))
      clearInterval(intervalRef.current)
      clearTimeout(timerRef.current)
      setIncidents([])
      setIncidentServices([])
    } finally {
      setLoadingIncidents(false)
    }
  }

  async function fetchPastIncidents() {
    // console.log('maybe fetching past incidents')

    if (features && features.data && features.meta && subdomainPopulated) {
      // console.log('fetching past incidents')
      const requestOptions = { timeout: sspPastIncidentsApiTimeout }

      const pastIncidentUrl = `api/ssp/incidents/pagination_list?page=${pageNumber}&include_incident_updates=true&include_affected_services=true&subdomain=${subdomain}`
      try {
        setLoadingPastIncidents(true)
        const response = await fetchWithTimeout(pastIncidentUrl, requestOptions)
        const body = await response.json()
        handleResponseErrors(response, body)
        setPastIncidents(body)
        setPageTotal(body.meta.total_pages)
      } catch (error) {
        pageError(error.message, 'pastIncidents', () => setErrorFlash(error.message, false))
        clearInterval(intervalRef.current)
        clearTimeout(timerRef.current)
        setPastIncidents([])
      } finally {
        setLoadingPastIncidents(false)
      }
    } else {
      setPastIncidents([])
    }
  }

  async function fetchMaintenance() {
    // console.log('fetching maint incidents')

    const requestOptions = { timeout: sspMaintenanceApiTimeout }

    let maintenanceUrl = `api/ssp/maintenance_incidents`
    if (subdomainPopulated) {
      maintenanceUrl += `?subdomain=${subdomain}`
    }

    try {
      setLoadingMaintenanceIncidents(true)
      const maintenanceResponse = await fetchWithTimeout(maintenanceUrl, requestOptions)
      const body = await maintenanceResponse.json()
      handleResponseErrors(maintenanceResponse, body)
      setMaintenanceIncidents(body)
    } catch (error) {
      pageError(error.message, 'maintIncidents', () => setErrorFlash(error.message, false))
      clearInterval(intervalRef.current)
      clearTimeout(timerRef.current)
      setMaintenanceIncidents([])
    } finally {
      setLoadingMaintenanceIncidents(false)
    }
  }

  /*
    Only hit the main APIs if features are already loaded. This
    ensures that the main page rendering (which wait until the main
    data is loaded) will render correctly based on the features being
    populated from the backend.
  */
  const fetchStatus = () => {
    // console.log('maybe fetching status')

    if (features && features.data && features.meta) {
      // console.log('fetching status')
      fetchServices()
      fetchIncidents()
      fetchMaintenance()
      getBanner()

      if (pageNumber !== 1) {
        setPageNumber(1) // calls fetchPastIncidents() too, after setting pageNumber
      } else {
        fetchPastIncidents()
      }
    }
  }

  const startRefreshOnInterval = () => {
    clearInterval(intervalRef.current)
    intervalRef.current = setInterval(() => {
      // console.log('refreshing on interval')
      fetchFeatures(setEnvironment, setFeatures, setLoadingFeatures)
    }, 300000)

    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      clearInterval(intervalRef.current)
    }, 6 * 60 * 60 * 1000)
  }

  /*
   Only thing fired on page load or subdomain change is a fetch to
   features. Then when features change (meaning a refresh), we hit the
   rest of the APIs to fill in the page.
  */
  useEffect(() => {
    // console.log('subdomain changed')
    setIsLeanDisplay(!subdomainPopulated)
    startRefreshOnInterval()
    fetchStatus()
  }, [subdomain])

  useEffect(() => {
    // console.log('pagenumber changed')
    fetchPastIncidents()
  }, [pageNumber])

  const domainSearch = curDomain => {
    const invalidChars = new RegExp(/[~`!#$%\^&*+=\[\]\\';,/{}|\\":<>\?]/)

    if (invalidChars.test(curDomain)) {
      searchError('Invalid subdomain')
    } else if (curDomain === subdomain) {
      fetchStatus()
    } else {
      setSubdomain(sanitizeSubdomainXSS(curDomain))
      removeFlash()
    }
  }

  const pastIncidentsDisplay = () => {
    if (loadingPastIncidents && !(loadingFeatures || loadingIncidents || loadingServices || loadingMaintenanceIncidents)) {
      return (
        <div style={{ width: "650px" }} >
          <XXXL>
            <Row style={{ marginTop: "30px", width:"50%" }}><Skeleton/></Row>
          </XXXL>
          <XL>
            <Row style={{ paddingTop: "32px", paddingBottom: "8px", width:"25%" }}><Skeleton/></Row>
          </XL>
          <MD>
            <Row style={{ paddingTop: "12px" }}><Skeleton/></Row>
            <Row style={{ marginTop: "10px" }}><Skeleton/></Row>
            <Row style={{ marginTop: "10px" }}><Skeleton/></Row>
          </MD>
          <XL>
            <Row style={{ paddingTop: "32px", paddingBottom: "8px", width:"25%" }}><Skeleton/></Row>
          </XL>
          <MD>
            <Row style={{ paddingTop: "12px" }}><Skeleton/></Row>
            <Row style={{ marginTop: "10px" }}><Skeleton/></Row>
            <Row style={{ marginTop: "10px" }}><Skeleton/></Row>
          </MD>
        </div>
      )
    } else if (!loadingPastIncidents && pastIncidents && pastIncidents.data) {
      return (
        <div>
          <Row justifyContent="center" alignItems="center" style={{ marginTop: "30px" }}>
            <PastIncidents
              pastIncidents={pastIncidents.data}
              incidentServices={pastIncidents.included.filter(i => i.type === "incidentService")}
              updates={pastIncidents.included.filter(i => i.type === "incidentUpdate")}
              inTimezone={browserTz}
            />
          </Row>
          <Row justifyContent="center" alignItems="center" style={{ marginTop: "30px" }}>
            <ArchiveAnchor pageNumber={pageNumber} onPageChange={setPageNumber} pageTotal={pageTotal} />
          </Row>
        </div>
      )
    }
    return (
      <Row justifyContent="center" alignItems="center" style={{ marginTop: "80px" }}>
        <XL>Unable to load past incidents</XL>
      </Row>
    )
  }

  const content = () => {
    if (loadingFeatures || loadingIncidents || loadingServices || loadingMaintenanceIncidents) {
      return (
        <div>
          <Row justifyContent="center" alignItems="center" style={{ marginTop: "80px" }}>
            <Spinner color="grey" size="100px" duration={1000} delayMS={750} />
          </Row>
        </div>
      )
    } else if (services && services.length > 0) {
      return (
        <div>
          <Row justifyContent="center" alignItems="center" style={{ marginTop: "4px", marginBottom: "80px" }}>
            <StyledLocationDiv>
              {location.data.attributes.name !== 'All' && location.data.attributes.name}
            </StyledLocationDiv>
          </Row>
          {maintenanceIncidents.data && (maintenanceIncidents.data.length > 0) &&
            <Row justifyContent="center" alignItems="center" style={{ marginBottom: "20px" }}>
              <MaintenanceBanner
                maintenanceIncidents={maintenanceIncidents.data}
                inTimezone={browserTz} />
            </Row>
          }
          <Row justifyContent="center" alignItems="center">
            <ServiceSummaryBanner incidents={incidents} subdomain={subdomain}
              inTimezone={browserTz}
              bannerValue={bannerValue} />
          </Row>
          <Row justifyContent="end" alignItems="end" style={{ marginBottom: "30px", marginTop: "2px" }}>
            <SystemStatus inTimezone={browserTz} />
          </Row>
          {/* Date range and older/newer nav is commented until design is set in future epics */}
          {/* <Row justifyContent="center" alignItems="center" style={{marginBottom: "30px"}}>
            <RangePager asOfDate={asOfDate} daysBack={daysBack} rangeOffset={rangeOffset} onRangeOffset={setRangeOffset} />
          </Row> */}
          <Row justifyContent="center" alignItems="center">
            <ServiceList
              services={services}
              incidents={incidents}
              incidentServices={incidentServices}
              inTimezone={browserTz}
              asOfDate={asOfDate}
              daysBack={daysBack}
              rangeOffset={rangeOffset}
              onRangeOffset={setRangeOffset}
              isLeanDisplay={isLeanDisplay}
              dateOffsetBugfixEnabled={dateOffsetBugfixEnabled}
            />
          </Row>
          {!isLeanDisplay && pastIncidentsDisplay()}
          <Row justifyContent="center" alignItems="center">
            <Col>
              <Footer/>
            </Col>
          </Row>
        </div>
      )
    }
    return (
      <Row justifyContent="center" alignItems="center" style={{ marginTop: "80px" }}>
        <XL>No services for subdomain</XL>
      </Row>
    )
  }

  return (
    <Grid>
      <Row justifyContent="center" alignItems="center">
        <Header
          env={environment}
          subdomain={subdomain}
          flash={flash}
          setSuccessFlash={setSuccessFlash}
          setErrorFlash={setErrorFlash}
          removeFlash={removeFlash}
        />
      </Row>
      <SubDomain
        subdomain={subdomain}
        onSearch={event => domainSearch(event)}
        flash={flash}
        setSuccessFlash={setSuccessFlash}
        setErrorFlash={setErrorFlash}
      />
      <Row justifyContent="center" alignItems="center">
        {content()}
      </Row>
    </Grid>
  )
}

export default App
